import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Star, Sparkles, Calendar, Users, BookOpen, Mail, Phone, MapPin, Clock, CheckCircle2, ArrowRight, Quote, Loader2, X, Moon, Sun, Headphones, FileText, Play, Download, ExternalLink, Gift, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
// Dialog removed - using custom modal
import { Layout } from "@/components/Layout";
import { OrderForm } from "@/components/OrderForm";
import { SlotMachine } from "@/components/SlotMachine";
import { InstagramFeed } from "@/components/InstagramFeed";
import { TestimonialsSection } from "@/components/TestimonialsSection";
import { SocialProofToasts } from "@/components/SocialProofToasts";
import { WheelAutoPopup } from "@/components/WheelAutoPopup";
import { StickyWhatsApp } from "@/components/StickyWhatsApp";
import { SERVICES, TESTIMONIALS, BLOG_POSTS, ROUTE_PATHS, ZODIAC_SIGNS } from "@/lib/index";
import { MONTHLY_HOROSCOPES_APRIL_2026, YEARLY_HOROSCOPES_2026 } from "@/lib/horoscopes";
import { IMAGES } from "@/assets/images";
import { springPresets, fadeInUp, hoverLift, staggerContainer, staggerItem } from "@/lib/motion";
import { supabase } from "@/integrations/supabase/client";

const serviceIcons: Record<string, typeof Star> = {
  natal: Star,
  transit: Sparkles,
  synastry: Users,
  solar: Calendar,
  consultation: BookOpen,
  package: CheckCircle2
};

export default function Home() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const wheelCode = searchParams.get("wheel_code") || "";
  const wheelDiscount = searchParams.get("discount") || "";
  const [keySequence, setKeySequence] = useState('');
  const [discountCode, setDiscountCode] = useState('');
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    service: '',
    birthDate: '',
    birthTime: '',
    birthPlace: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [paymentInfo, setPaymentInfo] = useState<any>(null);
  const [error, setError] = useState('');
const [selectedBlogPost, setSelectedBlogPost] = useState<typeof BLOG_POSTS[0] | null>(null);
  const [selectedZodiacSign, setSelectedZodiacSign] = useState<string>('Ovan');
  const [horoscopeType, setHoroscopeType] = useState<'monthly' | 'yearly'>('monthly');
  const [remainingPrizes, setRemainingPrizes] = useState<number | null>(null);
  const [heroVideo, setHeroVideo] = useState<{
    google_drive_id: string;
    title: string;
    description: string;
  } | null>(null);

  // Učitaj video iz baze
  useEffect(() => {
    const loadVideo = async () => {
      const { data, error } = await supabase
        .from('hero_video_2026_03_21')
        .select('google_drive_id, title, description')
        .eq('is_active', true)
        .single();
      
      if (data && !error) {
        setHeroVideo(data);
      }
    };
    
    loadVideo();
  }, []);

  // Učitaj preostale nagrade za točak
  useEffect(() => {
    const fetchRemainingPrizes = async () => {
      try {
        const today = new Date().toISOString().split('T')[0];
        const { data, error } = await supabase
          .from('wheel_spins_2026_03_20')
          .select('prize_type')
          .eq('spin_date', today)
          .neq('prize_type', 'LOSE');
        
        if (!error && data) {
          const dayOfWeek = new Date().getDay();
          const totalPrizes = dayOfWeek === 0 ? 11 : 10; // Nedeljom +1 FREE
          const remaining = totalPrizes - data.length;
          setRemainingPrizes(remaining > 0 ? remaining : 0);
        }
      } catch (err) {
        console.error('Error fetching remaining prizes:', err);
      }
    };

    fetchRemainingPrizes();
    // Refresh svakih 30 sekundi
    const interval = setInterval(fetchRemainingPrizes, 30000);
    return () => clearInterval(interval);
  }, []);

  
  // Keyboard listener za admin pristup
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      setKeySequence(prev => {
        const newSequence = (prev + e.key).slice(-8); // Čuva poslednjih 8 karaktera
        if (newSequence.toLowerCase() === 'admin123') {
          navigate(ROUTE_PATHS.ADMIN);
          return '';
        }
        return newSequence;
      });
    };

    window.addEventListener('keypress', handleKeyPress);
    return () => window.removeEventListener('keypress', handleKeyPress);
}, [navigate]);

  // Čitanje discount koda iz URL-a
  useEffect(() => {
    const codeFromUrl = searchParams.get('code');
    if (codeFromUrl) {
      setDiscountCode(codeFromUrl);
      // Scroll do contact sekcije (gde je forma)
      setTimeout(() => {
        const contactSection = document.getElementById('contact');
        if (contactSection) {
          contactSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 500);
    }
  }, [searchParams]);

  // Tracking poseta
  useEffect(() => {
    const trackVisit = async () => {
      // Generiši ili učitaj session ID
      let sessionId = sessionStorage.getItem('session_id');
      if (!sessionId) {
        sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        sessionStorage.setItem('session_id', sessionId);
      }

      // Detektuj izvor
      const referrer = document.referrer;
      let source = 'direct';
      if (referrer.includes('google')) source = 'google';
      else if (referrer.includes('facebook') || referrer.includes('instagram') || referrer.includes('twitter')) source = 'social';
      else if (referrer && referrer !== window.location.origin) source = 'other';

      try {
        await supabase.functions.invoke('track_visit_2026_03_19', {
          body: {
            sessionId,
            source,
            pagePath: window.location.pathname,
            userAgent: navigator.userAgent
          }
        });
      } catch (error) {
        console.error('Tracking error:', error);
      }
    };

    trackVisit();
  }, []);
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    console.log('Scrolling to:', sectionId, 'Element found:', !!element);
    if (element) {
      const headerOffset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      console.log('Scroll position:', offsetPosition);

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    } else {
      console.error('Element not found:', sectionId);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      // Validacija obaveznih polja
      if (!formData.firstName || !formData.lastName || !formData.email || 
          !formData.service || !formData.birthDate || !formData.birthTime || 
          !formData.birthPlace) {
        setError('Molimo popunite sva obavezna polja');
        setIsSubmitting(false);
        return;
      }

      // Poziv Edge funkcije
      const { data, error: functionError } = await supabase.functions.invoke('send_order_email_2026_03_17', {
        body: formData
      });

      if (functionError) {
        throw functionError;
      }

      if (data.error) {
        throw new Error(data.error);
      }

      // Uspešno poslato
      setPaymentInfo(data.paymentInfo);
      setShowSuccess(true);
      
      // Resetuj formu
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        service: '',
        birthDate: '',
        birthTime: '',
        birthPlace: '',
        message: ''
      });

    } catch (err: any) {
      console.error('Greška:', err);
      setError(err.message || 'Došlo je do greške. Molimo pokušajte ponovo.');
    } finally {
      setIsSubmitting(false);
    }
  };

return (
    <>
    {/* AUTO-POPUP ZA TOČAK SREĆE - MORA BITI PRVI! */}
    <WheelAutoPopup />
    
    
    {/* FLOATING DUGME ZA AUDIO PRIMERE - LEBDI NA DESNOJ STRANI */}

    {/* FLOATING DUGME - LEBDI NA DESNOJ STRANI */}
    <motion.div
      className="fixed right-6 top-1/2 -translate-y-1/2 z-50 hidden lg:block"
      initial={{ opacity: 0, x: 100 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: 2, duration: 0.5 }}
    >
      <motion.div
        animate={{
          y: [0, -10, 0],
        }}
        transition={{
          duration: 2,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      >
        <Button
          size="lg"
          className="flex flex-col items-center gap-2 px-6 py-8 bg-gradient-to-br from-accent via-primary to-accent shadow-2xl hover:shadow-accent/50 border-2 border-white/20 backdrop-blur-sm group relative overflow-hidden touch-manipulation active:scale-95"
          onClick={() => scrollToSection('examples')}
          type="button"
        >
          <motion.div
            className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent"
            animate={{
              x: ["-100%", "100%"]
            }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
              ease: "linear"
            }}
          />
          <motion.div
            animate={{
              scale: [1, 1.2, 1],
              rotate: [0, 5, -5, 0]
            }}
            transition={{
              duration: 2,
              repeat: Infinity
            }}
          >
            <Headphones className="w-8 h-8 relative z-10" />
          </motion.div>
          <span className="text-xs font-bold text-center relative z-10 leading-tight">
            AUDIO<br/>PRIMERI
          </span>
          <motion.div
            className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full"
            animate={{
              scale: [1, 1.3, 1],
              opacity: [1, 0.5, 1]
            }}
            transition={{
              duration: 1,
              repeat: Infinity
            }}
          />
        </Button>
      </motion.div>
    </motion.div>

    {/* FLOATING WHATSAPP DUGME - DONJE DESNO (UVEK VIDLJIVO) */}
    <motion.div
      className="fixed right-6 bottom-6 z-50"
      initial={{ opacity: 0, scale: 0 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: 1.5, duration: 0.5, type: "spring" }}
    >
      <motion.a
        href="https://chat.whatsapp.com/G18FEdGkdDWBcBfjTYJBTx"
        target="_blank"
        rel="noopener noreferrer"
        className="block"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
      >
        <motion.div
          className="relative"
          animate={{
            y: [0, -8, 0],
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        >
          {/* Pulsing Ring */}
          <motion.div
            className="absolute inset-0 bg-green-500 rounded-full"
            animate={{
              scale: [1, 1.3, 1],
              opacity: [0.5, 0, 0.5]
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeOut"
            }}
          />
          
          {/* Main Button */}
          <div className="relative w-16 h-16 bg-green-500 hover:bg-green-600 rounded-full shadow-2xl flex items-center justify-center transition-colors">
            <svg className="w-9 h-9 text-white" fill="currentColor" viewBox="0 0 24 24">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
            </svg>
            
            {/* Notification Badge */}
            <motion.div
              className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full border-2 border-white"
              animate={{
                scale: [1, 1.2, 1],
              }}
              transition={{
                duration: 1.5,
                repeat: Infinity
              }}
            />
          </div>
          
          {/* Tooltip */}
          <motion.div
            className="absolute right-full mr-3 top-1/2 -translate-y-1/2 bg-green-600 text-white px-4 py-2 rounded-lg shadow-lg whitespace-nowrap"
            initial={{ opacity: 0, x: 10 }}
            whileHover={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.2 }}
          >
            <span className="text-sm font-semibold">Pridruži se WhatsApp grupi! 🌟</span>
            <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-full w-0 h-0 border-t-8 border-t-transparent border-b-8 border-b-transparent border-l-8 border-l-green-600" />
          </motion.div>
        </motion.div>
      </motion.a>
    </motion.div>

    {/* Social Proof Toasts */}
    <SocialProofToasts />

    <Layout>
      <section id="hero" className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src={IMAGES.HERO_BACKGROUND_4} 
            alt="" 
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/60 to-background/90" />
        </div>
        
        <div className="absolute inset-0 z-0 opacity-20">
          {[...Array(50)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-1 h-1 bg-accent rounded-full"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                opacity: [0.2, 1, 0.2],
                scale: [1, 1.5, 1],
              }}
              transition={{
                duration: 2 + Math.random() * 3,
                repeat: Infinity,
                delay: Math.random() * 2,
              }}
            />
          ))}
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div 
            className="max-w-4xl mx-auto text-center"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={springPresets.gentle}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ ...springPresets.bouncy, delay: 0.2 }}
              className="inline-block mb-6"
            >
<Badge variant="secondary" className="px-6 py-2 text-base font-medium bg-gradient-to-r from-purple-500/20 via-pink-500/20 to-orange-500/20 text-primary border-2 border-primary/30 shadow-lg shadow-primary/20 backdrop-blur-sm animate-pulse">
                <Sparkles className="w-4 h-4 mr-2 inline animate-spin" />
                Profesionalne Astrološke Analize u PDF Formatu
              </Badge>
            </motion.div>

            <motion.h1 
              className="text-5xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-purple-400 via-pink-400 to-orange-400 bg-clip-text text-transparent animate-gradient bg-[length:200%_auto]"
              style={{ 
                fontFamily: "'Cormorant Garamond', serif",
                textShadow: '0 0 40px rgba(168, 85, 247, 0.4), 0 0 80px rgba(236, 72, 153, 0.3)'
              }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ ...springPresets.gentle, delay: 0.3 }}
            >
              Zvezde Znaju Ko Si Ti
            </motion.h1>

            <motion.p 
              className="text-xl md:text-2xl text-muted-foreground mb-8 max-w-2xl mx-auto leading-relaxed"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ ...springPresets.gentle, delay: 0.4 }}
            >
              Profesionalne astrološke analize u PDF formatu — ručno pisane, personalizovane samo za vas. Otkrijte svoju sudbinu. Planirajte budućnost.
            </motion.p>

            {/* Urgentnost - Dinamička */}
            <div className="mb-8 flex flex-col sm:flex-row gap-3 items-center justify-center">
              <motion.div
                className="inline-flex items-center gap-2 px-6 py-3 bg-orange-500/10 border border-orange-500/30 rounded-full text-orange-600 dark:text-orange-400"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ ...springPresets.gentle, delay: 0.45 }}
              >
                <Clock className="w-5 h-5" />
                <span className="font-semibold">Analiza gotova za 5 radnih dana</span>
              </motion.div>

              {remainingPrizes !== null && remainingPrizes > 0 && (
                <motion.div
                  className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border-2 border-yellow-500/50 rounded-full text-yellow-600 dark:text-yellow-400 animate-pulse"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ ...springPresets.gentle, delay: 0.6 }}
                >
                  <Gift className="w-5 h-5" />
                  <span className="font-bold">🎰 Još {remainingPrizes} nagrada danas!</span>
                </motion.div>
              )}
            </div>

            <motion.div 
              className="flex flex-col sm:flex-row gap-4 justify-center items-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ ...springPresets.gentle, delay: 0.5 }}
            >
              <Button 
                size="lg" 
                className="text-lg px-8 py-6 bg-gradient-to-r from-purple-600 via-pink-600 to-purple-600 hover:from-purple-500 hover:via-pink-500 hover:to-purple-500 shadow-2xl shadow-purple-500/50 hover:shadow-[0_0_40px_rgba(168,85,247,0.8)] transition-all duration-300 hover:scale-110 touch-manipulation active:scale-100 border-2 border-purple-400/50 animate-glow-pulse bg-[length:200%_auto] animate-gradient"
                onClick={() => scrollToSection('contact')}
                type="button"
              >
                <Sparkles className="mr-2 w-5 h-5 animate-spin" />
                Poručite Odmah
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button 
                size="lg" 
                variant="default" 
                className="text-xl font-bold px-10 py-7 bg-gradient-to-r from-yellow-400 via-orange-500 to-pink-500 hover:from-yellow-300 hover:via-orange-400 hover:to-pink-400 shadow-2xl shadow-orange-500/60 hover:shadow-[0_0_60px_rgba(251,191,36,0.9)] transition-all duration-300 hover:scale-110 touch-manipulation active:scale-105 border-2 border-yellow-300/60 animate-pulse hover:animate-none bg-[length:200%_auto] animate-gradient"
                onClick={() => scrollToSection('services')}
                type="button"
              >
                <Sparkles className="mr-2 w-6 h-6 animate-spin" />
                Pogledajte Usluge
                <Sparkles className="ml-2 w-6 h-6 animate-spin" />
              </Button>
            </motion.div>

            {/* TESTIMONIJALI - Social Proof */}
            <motion.div
              className="mt-12 max-w-4xl mx-auto"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ ...springPresets.gentle, delay: 0.6 }}
            >
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Testimonijal 1 */}
                <motion.div 
                  className="bg-gradient-to-br from-card/80 to-card/50 backdrop-blur-md border border-primary/20 rounded-xl p-5 hover:shadow-xl hover:shadow-primary/10 hover:border-primary/40 transition-all duration-300 hover:-translate-y-1"
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="flex gap-1 mb-3">
                    {[...Array(5)].map((_, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, scale: 0 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.7 + i * 0.1 }}
                      >
                        <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                      </motion.div>
                    ))}
                  </div>
                  <p className="text-sm text-muted-foreground mb-3 italic leading-relaxed">
                    "Neverovatno precizna analiza! Sve što je napisano se poklopilo sa mojim životom."
                  </p>
                  <p className="text-xs font-semibold text-primary">Marija P.</p>
                  <p className="text-xs text-muted-foreground">Natalna Karta</p>
                </motion.div>

                {/* Testimonijal 2 */}
                <motion.div 
                  className="bg-gradient-to-br from-card/80 to-card/50 backdrop-blur-md border border-accent/20 rounded-xl p-5 hover:shadow-xl hover:shadow-accent/10 hover:border-accent/40 transition-all duration-300 hover:-translate-y-1"
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="flex gap-1 mb-3">
                    {[...Array(5)].map((_, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, scale: 0 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.8 + i * 0.1 }}
                      >
                        <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                      </motion.div>
                    ))}
                  </div>
                  <p className="text-sm text-muted-foreground mb-3 italic leading-relaxed">
                    "Profesionalno, detaljno i tačno. Preporučujem svima!"
                  </p>
                  <p className="text-xs font-semibold text-accent">Stefan M.</p>
                  <p className="text-xs text-muted-foreground">Tranzitna Analiza</p>
                </motion.div>

                {/* Testimonijal 3 */}
                <motion.div 
                  className="bg-gradient-to-br from-card/80 to-card/50 backdrop-blur-md border border-primary/20 rounded-xl p-5 hover:shadow-xl hover:shadow-primary/10 hover:border-primary/40 transition-all duration-300 hover:-translate-y-1"
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="flex gap-1 mb-3">
                    {[...Array(5)].map((_, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, scale: 0 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.9 + i * 0.1 }}
                      >
                        <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                      </motion.div>
                    ))}
                  </div>
                  <p className="text-sm text-muted-foreground mb-3 italic leading-relaxed">
                    "Pomoglo mi je da razumem sebe i svoje odnose. Hvala!"
                  </p>
                  <p className="text-xs font-semibold text-primary">Ana K.</p>
                  <p className="text-xs text-muted-foreground">Sinastrijsko Poređenje</p>
                </motion.div>
              </div>
            </motion.div>

            {/* VELIKI TREPĆUĆI DUGME ZA PRIMERE */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ ...springPresets.bouncy, delay: 0.7 }}
              className="mt-12"
            >
              <motion.div
                animate={{
                  scale: [1, 1.05, 1],
                  boxShadow: [
                    "0 0 0 0 rgba(120, 119, 198, 0)",
                    "0 0 0 10px rgba(120, 119, 198, 0.3)",
                    "0 0 0 20px rgba(120, 119, 198, 0)"
                  ]
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              >
                <Button
                  size="lg"
                  variant="default"
                  className="relative text-base md:text-xl px-6 md:px-12 py-4 md:py-8 bg-gradient-to-r from-accent via-primary to-accent bg-[length:200%_100%] animate-gradient shadow-2xl hover:shadow-accent/50 transition-all duration-300 group overflow-hidden touch-manipulation active:scale-95 w-full sm:w-auto"
                  onClick={() => scrollToSection('examples')}
                  type="button"
                >
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                    animate={{
                      x: ["-100%", "100%"]
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      ease: "linear"
                    }}
                  />
                  <Sparkles className="w-5 h-5 md:w-6 md:h-6 mr-2 md:mr-3 animate-pulse" />
                  <span className="relative z-10 font-bold text-center leading-tight">
                    🎧 Pogledaj Šta Dobijaš<span className="hidden sm:inline"> - Audio & PDF Primeri</span><span className="sm:hidden">!</span>
                  </span>
                  <Play className="w-5 h-5 md:w-6 md:h-6 ml-2 md:ml-3 group-hover:scale-110 transition-transform" />
                </Button>
              </motion.div>
              <motion.p
                className="text-xs md:text-sm text-muted-foreground mt-3 flex items-center justify-center gap-2 px-4"
                animate={{ opacity: [0.5, 1, 0.5] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <Star className="w-3 h-3 md:w-4 md:h-4 text-accent hidden sm:inline" />
                <span className="text-center">Poslušaj audio snimke i pogledaj PDF primer</span>
                <Star className="w-3 h-3 md:w-4 md:h-4 text-accent hidden sm:inline" />
              </motion.p>
            </motion.div>


          </motion.div>
        </div>
      </section>

      {/* Pitaj Astrologa Daniela Banner - NOVO */}
      <section className="relative py-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-purple-900 via-pink-900 to-orange-900" />
        <div className="absolute inset-0 opacity-20">
          {[...Array(30)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-2 h-2 bg-yellow-400 rounded-full"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                opacity: [0, 1, 0],
                scale: [0, 1.5, 0],
              }}
              transition={{
                duration: 2 + Math.random() * 2,
                repeat: Infinity,
                delay: Math.random() * 2,
              }}
            />
          ))}
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-5xl mx-auto"
          >
            <div className="relative">
              {/* Glow effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-yellow-400/20 via-orange-400/20 to-pink-400/20 blur-3xl" />
              
              <div className="relative bg-gradient-to-br from-purple-800/40 via-pink-800/40 to-orange-800/40 backdrop-blur-sm border-4 border-yellow-400/50 rounded-3xl p-8 md:p-12 shadow-2xl">
                {/* Decorative corners */}
                <div className="absolute top-0 left-0 w-20 h-20 border-t-4 border-l-4 border-yellow-400 rounded-tl-3xl" />
                <div className="absolute top-0 right-0 w-20 h-20 border-t-4 border-r-4 border-yellow-400 rounded-tr-3xl" />
                <div className="absolute bottom-0 left-0 w-20 h-20 border-b-4 border-l-4 border-yellow-400 rounded-bl-3xl" />
                <div className="absolute bottom-0 right-0 w-20 h-20 border-b-4 border-r-4 border-yellow-400 rounded-br-3xl" />
                
                <div className="text-center space-y-6">
                  {/* Icon */}
                  <motion.div
                    animate={{
                      scale: [1, 1.1, 1],
                      rotate: [0, 5, -5, 0],
                    }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                    className="text-7xl md:text-8xl"
                    style={{
                      filter: "drop-shadow(0 0 30px rgba(251, 191, 36, 0.8))",
                    }}
                  >
                    🔮
                  </motion.div>
                  
                  {/* Main Title */}
                  <motion.h2
                    animate={{
                      textShadow: [
                        "0 0 20px rgba(251, 191, 36, 0.5)",
                        "0 0 40px rgba(251, 191, 36, 0.8)",
                        "0 0 20px rgba(251, 191, 36, 0.5)",
                      ],
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                    className="text-4xl md:text-6xl lg:text-7xl font-black text-transparent bg-clip-text bg-gradient-to-r from-yellow-200 via-orange-300 to-pink-300"
                    style={{
                      fontFamily: "'Playfair Display', serif",
                      letterSpacing: "0.02em",
                    }}
                  >
                    Pitaj Astrologa Daniela
                  </motion.h2>
                  
                  {/* Subtitle */}
                  <motion.p
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.3 }}
                    className="text-xl md:text-2xl text-yellow-100 font-semibold"
                  >
                    Brzi Odgovori • Pristupačne Cene • 24-48h
                  </motion.p>
                  
                  {/* Description */}
                  <p className="text-base md:text-lg text-white/90 max-w-2xl mx-auto leading-relaxed">
                    Postavite pitanje profesionalnom astrologu i dobijte personalizovan odgovor. 
                    Paketi od samo <span className="font-bold text-yellow-300">500 RSD</span>!
                  </p>
                  
                  {/* CTA Button */}
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Link to={ROUTE_PATHS.FORUM_CREDITS}>
                      <Button
                        size="lg"
                        className="text-xl md:text-2xl px-12 py-8 rounded-full bg-gradient-to-r from-yellow-400 via-orange-500 to-pink-500 hover:from-yellow-500 hover:via-orange-600 hover:to-pink-600 text-purple-900 font-black shadow-2xl border-4 border-yellow-300"
                        style={{
                          boxShadow: "0 0 50px rgba(251, 191, 36, 0.6), 0 0 100px rgba(251, 191, 36, 0.3)",
                        }}
                      >
                        <MessageCircle className="mr-3 h-8 w-8" />
                        Pitaj Odmah
                        <Sparkles className="ml-3 h-8 w-8 animate-spin" />
                      </Button>
                    </Link>
                  </motion.div>
                  
                  {/* Trust badges */}
                  <div className="flex flex-wrap items-center justify-center gap-6 pt-4">
                    <div className="flex items-center gap-2 text-yellow-200">
                      <CheckCircle2 className="w-5 h-5" />
                      <span className="text-sm font-medium">150+ Korisnika</span>
                    </div>
                    <div className="flex items-center gap-2 text-yellow-200">
                      <CheckCircle2 className="w-5 h-5" />
                      <span className="text-sm font-medium">300+ Odgovora</span>
                    </div>
                    <div className="flex items-center gap-2 text-yellow-200">
                      <Star className="w-5 h-5 fill-yellow-400" />
                      <span className="text-sm font-medium">4.9/5 Ocena</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <section id="about" className="py-24 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-background via-muted/30 to-background" />
        <div className="container mx-auto px-4 relative z-10">
          <motion.div 
            className="max-w-4xl mx-auto"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
          >
            <motion.div variants={staggerItem} className="text-center mb-16">
              <Badge variant="secondary" className="mb-4 px-4 py-1 bg-accent/10 text-accent border-accent/20">
                O Studiju
              </Badge>
              <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Vaš Vodič Kroz Kosmičku Mudrost
              </h2>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-8 items-center">
              <motion.div variants={staggerItem}>
                <Card className="overflow-hidden border-primary/10 shadow-lg">
                  <img 
                    src={IMAGES.NATAL_CHART_1} 
                    alt="Astrološka karta" 
                    className="w-full h-80 object-cover"
                  />
                </Card>
              </motion.div>

              <motion.div variants={staggerItem} className="space-y-6">
<p className="text-lg text-muted-foreground leading-relaxed">
                  Astro Aries Studio je profesionalni astrološki studio specijalizovan za izradu 
                  detaljnih astroloških analiza u profesionalno dizajniranom PDF formatu.
                </p>
                <p className="text-lg text-muted-foreground leading-relaxed">
                  Sa preko 10 godina iskustva u astrološkoj praksi, kombinujemo tradicionalnu mudrost 
                  sa savremenim pristupom. Sve analize se isporučuju u roku od 5 dana nakon uplate.
                </p>
                <div className="grid grid-cols-2 gap-4 pt-4">
                  <div className="text-center p-4 bg-primary/5 rounded-lg border border-primary/10">
                    <div className="text-3xl font-bold text-primary mb-1">500+</div>
                    <div className="text-sm text-muted-foreground">Zadovoljnih Klijenata</div>
                  </div>
                  <div className="text-center p-4 bg-accent/5 rounded-lg border border-accent/10">
                    <div className="text-3xl font-bold text-accent mb-1">10+</div>
                    <div className="text-sm text-muted-foreground">Godina Iskustva</div>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Video Testimonial Sekcija */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-background via-primary/5 to-background" />
        
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              {heroVideo?.title || 'Upoznajte Astrologa'}
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              {heroVideo?.description || 'Kratka poruka od osnivača Astro Aries Studio'}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="max-w-4xl mx-auto"
          >
            <div className="relative rounded-2xl overflow-hidden shadow-2xl border-4 border-primary/20 bg-card">
              {/* Video Player - Google Drive iframe */}
              <div className="relative aspect-video bg-black">
                {heroVideo ? (
                  <iframe
                    src={`https://drive.google.com/file/d/${heroVideo.google_drive_id}/preview`}
                    className="w-full h-full"
                    allow="autoplay"
                    allowFullScreen
                    title={heroVideo.title}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Loader2 className="w-12 h-12 animate-spin text-primary" />
                  </div>
                )}
              </div>
              
              {/* Video Info */}
              <div className="p-6 bg-gradient-to-r from-primary/10 to-accent/10">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                      <svg className="w-6 h-6 text-primary" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
                      </svg>
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg mb-1">Poruka od Astrologa</h3>
                    <p className="text-sm text-muted-foreground">
                      Saznajte više o našem pristupu astrologiji i kako možemo da vam pomognemo
                    </p>
                  </div>
                  <div className="flex gap-1">
                    {[...Array(5)].map((_, i) => (
                      <svg key={i} className="w-5 h-5 text-yellow-400 fill-current" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* CTA ispod videa */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="text-center mt-12"
          >
            <p className="text-muted-foreground mb-4">
              Spremni ste za vašu personalnu astrološku analizu?
            </p>
            <Button 
              size="lg" 
              onClick={() => scrollToSection('contact')}
              className="gap-2"
            >
              Poručite Svoju Analizu
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Primeri Analiza Sekcija */}
      <section id="examples" className="py-24 relative overflow-hidden bg-gradient-to-b from-muted/30 via-background to-muted/30">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(120,119,198,0.1),transparent_50%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_50%,rgba(255,107,107,0.1),transparent_50%)]" />
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <Badge variant="outline" className="mb-4 bg-primary/5">
              <Headphones className="w-3 h-3 mr-1" />
              Primeri Analiza
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Poslušajte i Pogledajte Primere
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Audio snimci i PDF primeri naših profesionalnih astroloških analiza. 
              Ovo je samo mali deo onoga što dobijate!
            </p>
          </motion.div>

          <div className="max-w-5xl mx-auto space-y-6">
            {/* Audio Primeri */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <h3 className="text-2xl font-semibold mb-6 flex items-center gap-2">
                <Headphones className="w-6 h-6 text-primary" />
                Audio Snimci Analiza
              </h3>
              <div className="grid md:grid-cols-3 gap-6">
                {/* Audio 1 */}
                <Card className="group hover:shadow-xl transition-all duration-300 border-2 hover:border-primary/50">
                  <CardHeader className="bg-gradient-to-br from-primary/10 to-accent/10">
                    <div className="flex items-center justify-between">
                      <Play className="w-8 h-8 text-primary" />
                      <Badge variant="secondary">Audio</Badge>
                    </div>
                    <CardTitle className="text-lg mt-4">Primer Analize 1</CardTitle>
                    <CardDescription>Profesionalna audio analiza natalne karte</CardDescription>
                  </CardHeader>
                  <CardContent className="pt-6">
                    <p className="text-sm text-muted-foreground mb-4">
                      Poslušajte kako zvuči profesionalna astrološka analiza sa detaljnim tumačenjem.
                    </p>
                    <Button 
                      variant="outline" 
                      className="w-full group-hover:bg-primary group-hover:text-primary-foreground transition-colors"
                      onClick={() => window.open('https://drive.google.com/file/d/1OtgAf2E-h-uFQGGfMwsBrJdC-v9R1p84/view?usp=sharing', '_blank')}
                    >
                      <Headphones className="w-4 h-4 mr-2" />
                      Poslušaj Primer
                      <ExternalLink className="w-3 h-3 ml-2" />
                    </Button>
                  </CardContent>
                </Card>

                {/* Audio 2 */}
                <Card className="group hover:shadow-xl transition-all duration-300 border-2 hover:border-primary/50">
                  <CardHeader className="bg-gradient-to-br from-accent/10 to-secondary/10">
                    <div className="flex items-center justify-between">
                      <Play className="w-8 h-8 text-accent" />
                      <Badge variant="secondary">Audio</Badge>
                    </div>
                    <CardTitle className="text-lg mt-4">Primer Analize 2</CardTitle>
                    <CardDescription>Detaljna analiza planetarnih pozicija</CardDescription>
                  </CardHeader>
                  <CardContent className="pt-6">
                    <p className="text-sm text-muted-foreground mb-4">
                      Primer kako tumačimo planete, aspekte i kuće u vašoj karti.
                    </p>
                    <Button 
                      variant="outline" 
                      className="w-full group-hover:bg-accent group-hover:text-accent-foreground transition-colors"
                      onClick={() => window.open('https://drive.google.com/file/d/1CcDJM_UQ_rP_81uJP_TxO3qo23WF7igp/view?usp=drive_link', '_blank')}
                    >
                      <Headphones className="w-4 h-4 mr-2" />
                      Poslušaj Primer
                      <ExternalLink className="w-3 h-3 ml-2" />
                    </Button>
                  </CardContent>
                </Card>

                {/* Audio 3 */}
                <Card className="group hover:shadow-xl transition-all duration-300 border-2 hover:border-primary/50">
                  <CardHeader className="bg-gradient-to-br from-secondary/10 to-primary/10">
                    <div className="flex items-center justify-between">
                      <Play className="w-8 h-8 text-secondary" />
                      <Badge variant="secondary">Audio</Badge>
                    </div>
                    <CardTitle className="text-lg mt-4">Primer Analize 3</CardTitle>
                    <CardDescription>Tumačenje životnih lekcija i potencijala</CardDescription>
                  </CardHeader>
                  <CardContent className="pt-6">
                    <p className="text-sm text-muted-foreground mb-4">
                      Slušajte kako otkrivamo vaše talente, izazove i životni put.
                    </p>
                    <Button 
                      variant="outline" 
                      className="w-full group-hover:bg-secondary group-hover:text-secondary-foreground transition-colors"
                      onClick={() => window.open('https://drive.google.com/file/d/1WZyXff3zj2u0MgQwc4AmbHN1Nu54NCam/view?usp=drive_link', '_blank')}
                    >
                      <Headphones className="w-4 h-4 mr-2" />
                      Poslušaj Primer
                      <ExternalLink className="w-3 h-3 ml-2" />
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </motion.div>

            {/* PDF Primer */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="mt-12"
            >
              <h3 className="text-2xl font-semibold mb-6 flex items-center gap-2">
                <FileText className="w-6 h-6 text-primary" />
                PDF Primer Analize
              </h3>
              <Card className="group hover:shadow-2xl transition-all duration-300 border-2 hover:border-primary/50 bg-gradient-to-br from-primary/5 via-background to-accent/5">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 rounded-lg bg-gradient-to-br from-primary to-accent flex items-center justify-center shadow-lg">
                        <FileText className="w-8 h-8 text-primary-foreground" />
                      </div>
                      <div>
                        <CardTitle className="text-2xl">Profesionalno Dizajniran PDF</CardTitle>
                        <CardDescription className="text-base mt-1">
                          Pogledajte kako izgleda vaša analiza
                        </CardDescription>
                      </div>
                    </div>
                    <Badge variant="default" className="text-sm px-3 py-1">
                      PDF Primer
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <Alert className="border-primary/20 bg-primary/5">
                    <Sparkles className="w-4 h-4 text-primary" />
                    <AlertDescription>
                      <strong>Napomena:</strong> Ovo je samo mali deo onoga što dobijate! 
                      Kompletna analiza sadrži 20-30 strana detaljnog tumačenja vaše natalne karte.
                    </AlertDescription>
                  </Alert>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <h4 className="font-semibold flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-primary" />
                        Šta sadrži PDF:
                      </h4>
                      <ul className="space-y-1 text-sm text-muted-foreground ml-6">
                        <li>• Profesionalni dizajn i layout</li>
                        <li>• Grafički prikaz natalne karte</li>
                        <li>• Detaljno tumačenje svih planeta</li>
                        <li>• Analiza aspekata i kuća</li>
                        <li>• Životne lekcije i potencijali</li>
                        <li>• Praktični saveti za razvoj</li>
                      </ul>
                    </div>
                    <div className="space-y-2">
                      <h4 className="font-semibold flex items-center gap-2">
                        <Star className="w-4 h-4 text-accent" />
                        Karakteristike:
                      </h4>
                      <ul className="space-y-1 text-sm text-muted-foreground ml-6">
                        <li>• 20-30 strana sadržaja</li>
                        <li>• Visoka rezolucija za štampu</li>
                        <li>• Lako čitljiv format</li>
                        <li>• Personalizovano za vas</li>
                        <li>• Isporuka na email</li>
                        <li>• Trajno vaše za čuvanje</li>
                      </ul>
                    </div>
                  </div>

                  <div className="flex gap-4 pt-4">
                    <Button 
                      size="lg"
                      className="flex-1 group-hover:shadow-lg transition-all"
                      onClick={() => window.open('https://drive.google.com/file/d/1Q-AzUY9AxZpFg31D8ulKQ_vGA_6DvqWC/view?usp=drive_link', '_blank')}
                    >
                      <FileText className="w-5 h-5 mr-2" />
                      Pogledaj PDF Primer
                      <ExternalLink className="w-4 h-4 ml-2" />
                    </Button>
                    <Button 
                      size="lg"
                      variant="outline"
                      onClick={() => {
                        const contactSection = document.getElementById('contact');
                        contactSection?.scrollIntoView({ behavior: 'smooth' });
                      }}
                    >
                      Poruči Svoju Analizu
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Info Box */}
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="mt-8"
            >
              <Card className="border-accent/20 bg-gradient-to-r from-accent/5 to-primary/5">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
                      <Sparkles className="w-6 h-6 text-accent" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-lg mb-2">Zašto su naše analize posebne?</h4>
                      <p className="text-muted-foreground">
                        Svaka analiza je ručno rađena sa pažnjom i ljubavlju. Ne koristimo automatske generatore - 
                        svaka reč je napisana specifično za vas, uzimajući u obzir sve nijanse vaše natalne karte. 
                        Dobijate ne samo podatke, već i duboko razumevanje sebe, svojih talenata i životnog puta.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      <section id="services" className="py-24 relative">
        <div className="absolute inset-0">
          <img 
            src={IMAGES.COSMIC_BG_1} 
            alt="" 
            className="w-full h-full object-cover opacity-5"
          />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <motion.div 
            className="text-center mb-16"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
          >
            <motion.div variants={staggerItem}>
              <Badge variant="secondary" className="mb-4 px-4 py-1 bg-primary/10 text-primary border-primary/20">
                Naše Usluge
              </Badge>
            </motion.div>
            <motion.h2 
              variants={staggerItem}
              className="text-4xl md:text-5xl font-bold mb-6" 
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
Astrološke Analize
            </motion.h2>
            <motion.p 
              variants={staggerItem}
              className="text-xl text-muted-foreground max-w-2xl mx-auto"
            >
              Izaberite analizu koja odgovara vašim potrebama. Sve analize se isporučuju u PDF formatu.
            </motion.p>
          </motion.div>

          <motion.div 
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
          >
            {SERVICES.map((service) => {
              const IconComponent = serviceIcons[service.icon] || Star;
              return (
                <motion.div 
                  key={service.id} 
                  variants={staggerItem}
                  whileHover={{ y: -8, transition: { duration: 0.3 } }}
                >
                  <Card 
                    className="h-full flex flex-col border-2 border-purple-500/30 hover:border-pink-500/60 transition-all duration-500 group relative overflow-hidden bg-gradient-to-br from-purple-900/20 via-pink-900/10 to-orange-900/20 backdrop-blur-xl shadow-2xl hover:shadow-[0_0_50px_rgba(168,85,247,0.4)] animate-glow-pulse"
                  >
                    {/* Animated gradient overlay */}
                    <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 via-pink-500/10 to-orange-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500 animate-gradient bg-[length:200%_200%]" />
                    
                    {/* Floating particles */}
                    <div className="absolute inset-0 overflow-hidden pointer-events-none">
                      {[...Array(5)].map((_, i) => (
                        <motion.div
                          key={i}
                          className="absolute w-2 h-2 bg-gradient-to-r from-purple-400 to-pink-400 rounded-full opacity-20"
                          style={{
                            left: `${20 + i * 20}%`,
                            top: `${30 + i * 10}%`,
                          }}
                          animate={{
                            y: [-20, 20, -20],
                            opacity: [0.2, 0.5, 0.2],
                          }}
                          transition={{
                            duration: 3 + i,
                            repeat: Infinity,
                            ease: "easeInOut",
                          }}
                        />
                      ))}
                    </div>
                    
                    <CardHeader className="relative z-10">
                      <motion.div 
                        className="w-14 h-14 rounded-xl bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg"
                        whileHover={{ rotate: [0, -10, 10, -10, 0] }}
                        transition={{ duration: 0.5 }}
                      >
                        <IconComponent className="w-7 h-7 text-primary group-hover:text-accent transition-colors duration-300" />
                      </motion.div>
                      <CardTitle className="text-2xl group-hover:text-primary transition-colors duration-300" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                        {service.title}
                      </CardTitle>
                      <CardDescription className="text-base">
                        {service.description}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="flex-1 relative z-10">
                      <div className="flex items-center justify-between mb-4 pb-4 border-b border-border/50 group-hover:border-primary/30 transition-colors duration-300">
                        <div>
                          <div className="text-3xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">{service.price}</div>
                          <div className="text-sm text-muted-foreground">{service.duration}</div>
                        </div>
                      </div>
                      <ul className="space-y-3">
                        {service.features.map((feature, idx) => (
                          <motion.li 
                            key={idx} 
                            className="flex items-start gap-2 text-sm"
                            initial={{ opacity: 0, x: -10 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ delay: idx * 0.1 }}
                            viewport={{ once: true }}
                          >
                            <CheckCircle2 className="w-4 h-4 text-accent mt-0.5 flex-shrink-0 group-hover:scale-110 transition-transform duration-300" />
                            <span className="group-hover:text-foreground transition-colors duration-300">{feature}</span>
                          </motion.li>
                        ))}
                      </ul>
                    </CardContent>
                    <CardFooter className="flex flex-col gap-3 relative z-10">
                      <Button className="w-full bg-gradient-to-r from-green-600 to-green-500 hover:from-green-500 hover:to-green-400 shadow-lg hover:shadow-xl hover:shadow-green-500/30 transition-all duration-300 group/btn" asChild>
                        <a href="https://wa.me/381653471080" target="_blank" rel="noopener noreferrer">
                          <svg className="w-4 h-4 mr-2 group-hover/btn:scale-110 transition-transform duration-300" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                          </svg>
                          Poruči na WhatsApp
                        </a>
                      </Button>
                      <Button className="w-full border-primary/30 hover:border-primary hover:bg-primary/10 transition-all duration-300" variant="outline" onClick={() => scrollToSection('contact')}>
                        Poruči Formom
                      </Button>
                    </CardFooter>
                  </Card>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* Forum Section */}
      <section id="forum" className="py-24 relative overflow-hidden bg-gradient-to-b from-primary/5 via-accent/5 to-background">
        <div className="absolute inset-0">
          <img 
            src={IMAGES.COSMIC_BG_2} 
            alt="" 
            className="w-full h-full object-cover opacity-10"
          />
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <motion.div 
            className="text-center mb-16"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
          >
            <motion.div variants={staggerItem}>
              <Badge variant="secondary" className="mb-4 px-4 py-1 bg-accent/10 text-accent border-accent/20">
                Novo! 🌟
              </Badge>
            </motion.div>
            <motion.h2 
              variants={staggerItem}
              className="text-4xl md:text-5xl font-bold mb-6" 
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              Astrološki Forum
            </motion.h2>
            <motion.p 
              variants={staggerItem}
              className="text-xl text-muted-foreground max-w-3xl mx-auto"
            >
              Postavite pitanje i dobijte personalizovan astrološki odgovor od profesionalnog astrologa
            </motion.p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            {/* Left: Benefits */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="space-y-8"
            >
              <div>
                <h3 className="text-3xl font-bold mb-6">Zašto koristiti Forum?</h3>
                <div className="space-y-6">
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                      <CheckCircle2 className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-lg mb-2">Brzi Odgovori</h4>
                      <p className="text-muted-foreground">
                        Dobijte odgovor na vaše pitanje u roku od 24-48h. Bez čekanja, bez komplikacija.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center">
                      <Star className="w-6 h-6 text-accent" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-lg mb-2">Personalizovani Odgovori</h4>
                      <p className="text-muted-foreground">
                        Svaki odgovor je prilagođen vašem natalnom horoskopu - datum, vreme i mesto rođenja.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-green-500/10 flex items-center justify-center">
                      <Users className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-lg mb-2">Učite od Drugih</h4>
                      <p className="text-muted-foreground">
                        Pregledajte odgovore na pitanja drugih korisnika i proširite svoje znanje.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-blue-500/10 flex items-center justify-center">
                      <Gift className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-lg mb-2">Pristupačne Cene</h4>
                      <p className="text-muted-foreground">
                        Paketi kredita od 500 RSD. Jedan kredit = jedno pitanje. <strong className="text-foreground">Krediti važe 30 dana od kupovine.</strong>
                      </p>
                    </div>
                  </div>

                  <div className="mt-6 p-4 bg-gradient-to-br from-primary/10 via-accent/5 to-primary/5 border border-primary/20 rounded-lg">
                    <div className="flex items-start gap-3">
                      <Clock className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <div>
                        <h5 className="font-semibold text-base mb-2 flex items-center gap-2">
                          Zašto 30 Dana? 
                          <Sparkles className="w-4 h-4 text-accent" />
                        </h5>
                        <div className="space-y-2 text-sm text-muted-foreground">
                          <p>
                            <strong className="text-foreground">Vaš astrolog je tu za vas ceo mesec!</strong> Sačuvajte kredite za bitne trenutke - kada vam je zaista potrebna pomoć, kada donosite važne odluke, ili kada osećate da vam je potrebno astrološko vođenje.
                          </p>
                          <p>
                            💫 <strong className="text-foreground">Fleksibilnost:</strong> Nemate obavezu da potrošite sve kredite odmah. Koristite ih kada vam zaista trebaju - za ljubavne dileme, poslovne odluke, ili životne prekretnice.
                          </p>
                          <p>
                            ⏰ <strong className="text-foreground">Važno:</strong> Potrudite se da do kraja perioda iskoristite svoje kredite kako ne bi propali. Planirajte pitanja, razmislite šta vas muči, i iskoristite priliku da dobijete profesionalne odgovore!
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Right: CTA Card */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <Card className="border-primary/20 shadow-2xl overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-accent/5 to-transparent" />
                <CardHeader className="relative">
                  <CardTitle className="text-2xl">Kako Funkcioniše?</CardTitle>
                  <CardDescription className="text-base">
                    Jednostavan proces u 4 koraka
                  </CardDescription>
                </CardHeader>
                <CardContent className="relative space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold">
                      1
                    </div>
                    <div>
                      <h5 className="font-semibold mb-1">Kupite Kredite</h5>
                      <p className="text-sm text-muted-foreground">
                        Izaberite paket kredita koji vam odgovara (Starter, Popularan, VIP, Premium)
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-accent text-accent-foreground flex items-center justify-center font-bold">
                      2
                    </div>
                    <div>
                      <h5 className="font-semibold mb-1">Postavite Pitanje</h5>
                      <p className="text-sm text-muted-foreground">
                        Unesite datum, vreme i mesto rođenja + vaše pitanje (ljubav, karijera, zdravlje, finansije)
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-green-600 text-white flex items-center justify-center font-bold">
                      3
                    </div>
                    <div>
                      <h5 className="font-semibold mb-1">Dobijte Odgovor</h5>
                      <p className="text-sm text-muted-foreground">
                        Profesionalni astrolog analizira vaš natalni horoskop i daje detaljan odgovor
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold">
                      4
                    </div>
                    <div>
                      <h5 className="font-semibold mb-1">Pregledajte Forum</h5>
                      <p className="text-sm text-muted-foreground">
                        Vaš odgovor je vidljiv na forumu. Možete pregledati i odgovore na druga pitanja.
                      </p>
                    </div>
                  </div>

                  <div className="pt-4 border-t">
                    <div className="bg-muted/50 rounded-lg p-4 mb-4">
                      <h5 className="font-semibold mb-2 flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-primary" />
                        Paketi Kredita
                      </h5>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Starter (5 kredita)</span>
                          <span className="font-semibold">500 RSD</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Popularan (10 kredita)</span>
                          <span className="font-semibold">900 RSD</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">VIP (20 kredita)</span>
                          <span className="font-semibold">1,600 RSD</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Premium (50 kredita)</span>
                          <span className="font-semibold">3,500 RSD</span>
                        </div>
                      </div>
                      <div className="mt-3 pt-3 border-t border-border/50">
                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          Krediti važe <strong className="text-foreground mx-1">30 dana</strong> od kupovine
                        </p>
                      </div>
                    </div>

                    <Button 
                      className="w-full text-lg py-6 bg-gradient-to-r from-primary to-accent hover:opacity-90 transition-opacity"
                      onClick={() => navigate(ROUTE_PATHS.FORUM_CREDITS)}
                    >
                      Počnite Sada
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </Button>
                    
                    <div className="mt-4 p-4 bg-gradient-to-br from-accent/10 via-primary/5 to-accent/5 border border-accent/30 rounded-lg space-y-2">
                      <p className="text-xs text-center text-muted-foreground">
                        ✨ <strong className="text-foreground">Vaš astrolog je tu za vas!</strong> Krediti važe <strong className="text-foreground">30 dana</strong> - sačuvajte ih za bitne trenutke kada vam je zaista potrebna pomoć.
                      </p>
                      <p className="text-xs text-center text-muted-foreground">
                        💫 Koristite kredite kada donosite važne odluke ili kada osećate da vam je potrebno astrološko vođenje. Nemojte dozvoliti da propuste - iskoristite ih na vreme!
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>

          {/* Bottom: Social Proof */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-16 text-center"
          >
            <div className="inline-flex items-center gap-8 bg-card/50 backdrop-blur-sm border border-border/50 rounded-full px-8 py-4">
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-primary" />
                <div className="text-left">
                  <p className="text-2xl font-bold">150+</p>
                  <p className="text-xs text-muted-foreground">Korisnika</p>
                </div>
              </div>
              <div className="h-8 w-px bg-border" />
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
                <div className="text-left">
                  <p className="text-2xl font-bold">300+</p>
                  <p className="text-xs text-muted-foreground">Odgovora</p>
                </div>
              </div>
              <div className="h-8 w-px bg-border" />
              <div className="flex items-center gap-2">
                <Star className="w-5 h-5 text-yellow-500" />
                <div className="text-left">
                  <p className="text-2xl font-bold">4.9/5</p>
                  <p className="text-xs text-muted-foreground">Ocena</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
      <section id="process" className="py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <motion.div 
            className="text-center mb-16"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
          >
            <motion.div variants={staggerItem}>
              <Badge variant="secondary" className="mb-4 px-4 py-1 bg-accent/10 text-accent border-accent/20">
                Kako Funkcioniše
              </Badge>
            </motion.div>
            <motion.h2 
              variants={staggerItem}
              className="text-4xl md:text-5xl font-bold mb-6" 
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
Proces Poručivanja
            </motion.h2>
          </motion.div>

          <motion.div 
            className="grid md:grid-cols-4 gap-8 max-w-6xl mx-auto"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
          >
{[
              {
                step: "01",
                title: "Kontakt",
                description: "Kontaktirajte nas putem WhatsApp-a, emaila ili kontakt forme sa vašim podacima o rođenju."
              },
              {
                step: "02",
                title: "Uplata",
                description: "Nakon potvrde, vršite uplatu za odabranu uslugu i šaljete potvrdu."
              },
              {
                step: "03",
                title: "Izrada",
                description: "Analiziram vašu natalnu kartu i pripremam profesionalno dizajniran PDF dokument."
              },
              {
                step: "04",
                title: "Isporuka",
                description: "Dobijate kompletnu analizu u PDF formatu na vaš email u roku od 5 dana."
              }
            ].map((item, idx) => (
              <motion.div key={idx} variants={staggerItem}>
                <Card className="text-center h-full border-primary/10 hover:border-primary/30 transition-all duration-300">
                  <CardHeader>
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center mx-auto mb-4 text-2xl font-bold text-primary-foreground">
                      {item.step}
                    </div>
                    <CardTitle className="text-xl" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                      {item.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">{item.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Stara testimonials sekcija uklonjena - koristimo novu TestimonialsSection komponentu */}

      <section id="blog" className="py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <motion.div 
            className="text-center mb-16"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
          >
            <motion.div variants={staggerItem}>
              <Badge variant="secondary" className="mb-4 px-4 py-1 bg-accent/10 text-accent border-accent/20">
                Blog
              </Badge>
            </motion.div>
            <motion.h2 
              variants={staggerItem}
              className="text-4xl md:text-5xl font-bold mb-6" 
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              Astrološki Sadržaj
            </motion.h2>
            <motion.p 
              variants={staggerItem}
              className="text-xl text-muted-foreground max-w-2xl mx-auto"
            >
              Edukativni članci o astrologiji, planetarnim uticajima i duhovnom razvoju
            </motion.p>
          </motion.div>

          <motion.div 
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
          >
{BLOG_POSTS.map((post) => {
              const imageKey = post.image as keyof typeof IMAGES;
              const isExpanded = selectedBlogPost?.id === post.id;
              return (
                <motion.div key={post.id} variants={staggerItem}>
                  <Card className="overflow-hidden border-primary/10 hover:border-primary/30 transition-all duration-300 group">
                    <div className="relative overflow-hidden">
                      <img 
                        src={IMAGES[imageKey]} 
                        alt={post.title} 
                        className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      <Badge className="absolute top-4 left-4 bg-primary/90 text-primary-foreground">
                        {post.category}
                      </Badge>
                    </div>
                    <CardHeader>
                      <div className="flex items-center gap-4 text-sm text-muted-foreground mb-2">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {new Date(post.date).toLocaleDateString('sr-RS', { 
                            year: 'numeric', 
                            month: 'short', 
                            day: 'numeric' 
                          })}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {post.readTime}
                        </span>
                      </div>
                      <CardTitle className="text-xl group-hover:text-primary transition-colors" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                        {post.title}
                      </CardTitle>
                    </CardHeader>
<CardContent>
                      <p className="text-muted-foreground mb-4">{post.excerpt}</p>
                      
                      {/* Expanded Content */}
                      {isExpanded && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.3 }}
                          className="mt-4 pt-4 border-t space-y-4"
                        >
                          {post.content.split('\n\n').map((paragraph, index) => {
                            // Check if it's a heading
                            if (paragraph.startsWith('**') && paragraph.endsWith('**')) {
                              const text = paragraph.replace(/\*\*/g, '');
                              return (
                                <h3 key={index} className="text-lg md:text-xl font-bold mt-4 mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                                  {text}
                                </h3>
                              );
                            }
                            
                            // Check if it's a bullet list
                            if (paragraph.includes('\u2022')) {
                              const items = paragraph.split('\n').filter(line => line.trim());
                              return (
                                <ul key={index} className="space-y-1 my-2 ml-4">
                                  {items.map((item, i) => (
                                    <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                                      <span className="text-primary mt-1 flex-shrink-0">•</span>
                                      <span>{item.replace('\u2022', '').trim()}</span>
                                    </li>
                                  ))}
                                </ul>
                              );
                            }
                            
                            // Regular paragraph
                            return (
                              <p key={index} className="text-muted-foreground text-sm leading-relaxed">
                                {paragraph}
                              </p>
                            );
                          })}
                          
                          <Button 
                            onClick={() => scrollToSection('contact')}
                            className="w-full mt-6"
                          >
                            Poručite Konsultaciju
                            <ArrowRight className="ml-2 w-4 h-4" />
                          </Button>
                        </motion.div>
                      )}
                    </CardContent>
                    <CardFooter>
                      <Button 
                        variant="ghost" 
                        className="w-full group-hover:bg-primary/5"
                        onClick={() => {
                          if (isExpanded) {
                            setSelectedBlogPost(null);
                          } else {
                            setSelectedBlogPost(post);
                          }
                        }}
                      >
                        {isExpanded ? 'Sakrij' : 'Pogledaj Više'}
                        <ArrowRight className={`ml-2 w-4 h-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                      </Button>
                    </CardFooter>
                  </Card>
                </motion.div>
              );
            })}
</motion.div>
        </div>
      </section>

      <section className="py-24 bg-gradient-to-b from-primary/5 to-background relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          {[...Array(30)].map((_, i) => (
            <motion.div
              key={`whatsapp-star-${i}`}
              className="absolute w-1 h-1 bg-accent rounded-full"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                opacity: [0.2, 1, 0.2],
              }}
              transition={{
                duration: 3 + Math.random() * 2,
                repeat: Infinity,
                delay: Math.random() * 2,
              }}
            />
          ))}
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <motion.div 
            className="max-w-3xl mx-auto text-center"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
          >
            <motion.div variants={staggerItem}>
              <Badge variant="secondary" className="mb-4 px-4 py-1 bg-accent/10 text-accent border-accent/20">
                Pridružite Se Zajednici
              </Badge>
            </motion.div>
            <motion.h2 
              variants={staggerItem}
              className="text-4xl md:text-5xl font-bold mb-6" 
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              WhatsApp Grupa
            </motion.h2>
<motion.p 
              variants={staggerItem}
              className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto"
            >
              Pridružite se našoj ekskluzivnoj WhatsApp grupi za ljubitelje astrologije! 
              Dobijajte svakodnevne savete, analize trenutnih tranzita i obaveštenja od astrologa uživo.
            </motion.p>
            
            {/* WhatsApp Grupa Kartica */}
            <motion.div variants={staggerItem} className="mb-8 max-w-2xl mx-auto">
              <Card className="border-green-500/30 bg-gradient-to-br from-green-50/50 to-emerald-50/50 dark:from-green-950/20 dark:to-emerald-950/20 backdrop-blur overflow-hidden">
                <CardContent className="p-8">
                  <div className="flex flex-col md:flex-row items-center gap-6">
                    {/* WhatsApp Ikona */}
                    <div className="flex-shrink-0">
                      <div className="w-24 h-24 bg-green-500 rounded-full flex items-center justify-center shadow-lg">
                        <svg className="w-14 h-14 text-white" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                        </svg>
                      </div>
                    </div>
                    
                    {/* Tekst */}
                    <div className="flex-1 text-center md:text-left">
                      <h3 className="text-2xl font-bold mb-3 text-green-700 dark:text-green-400">
                        ⭐ Zvezde Uživo - Svaki Dan!
                      </h3>
                      <ul className="text-left space-y-2 mb-4">
                        <li className="flex items-start gap-2">
                          <span className="text-green-600 dark:text-green-400 mt-1">✓</span>
                          <span className="text-muted-foreground">Dnevni astrološki saveti od profesionalnog astrologa</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-green-600 dark:text-green-400 mt-1">✓</span>
                          <span className="text-muted-foreground">Analize trenutnih tranzita i retrograda</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-green-600 dark:text-green-400 mt-1">✓</span>
                          <span className="text-muted-foreground">Obaveštenja o važnim astrološkim događajima</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-green-600 dark:text-green-400 mt-1">✓</span>
                          <span className="text-muted-foreground">Ekskluzivna zajednica ljubitelja astrologije</span>
                        </li>
                      </ul>
                      <p className="text-sm text-green-700 dark:text-green-400 font-semibold">
                        🎁 Potpuno besplatno - Pridružite se danas!
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
            
            
            <motion.div 
              variants={staggerItem} 
              className="flex flex-col sm:flex-row gap-4 justify-center items-center"
            >
              <motion.div
                animate={{
                  scale: [1, 1.05, 1],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              >
                <Button size="lg" className="text-lg px-8 py-6 bg-green-600 hover:bg-green-700 shadow-lg relative overflow-hidden group" asChild>
                  <a href="https://chat.whatsapp.com/G18FEdGkdDWBcBfjTYJBTx" target="_blank" rel="noopener noreferrer">
                    {/* Shine Effect */}
                    <motion.div
                      className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                      animate={{
                        x: ['-100%', '200%']
                      }}
                      transition={{
                        duration: 3,
                        repeat: Infinity,
                        ease: "linear"
                      }}
                    />
                    <svg className="w-5 h-5 mr-2 relative z-10" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                    </svg>
                    <span className="relative z-10">Pridruži Se Grupi</span>
                  </a>
                </Button>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </section>


      {/* Horoskopi Sekcija */}
      <section id="horoscopes" className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-transparent to-primary/5" />
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <Badge variant="outline" className="mb-4">
              <Moon className="w-3 h-3 mr-1" />
              Horoskopi
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Mesečni i Godišnji Horoskopi
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Profesionalno napisani horoskopi za svaki znak, bazirani na realnim astrološkim tranzitima i pozicijama planeta
            </p>
          </motion.div>

          <div className="max-w-6xl mx-auto">
            {/* Tip Horoskopa Tabs - Istaknuto */}
            <div className="mb-12">
              <p className="text-center text-sm text-muted-foreground mb-4">
                Izaberite tip horoskopa:
              </p>
              <Tabs value={horoscopeType} onValueChange={(value) => setHoroscopeType(value as 'monthly' | 'yearly')} className="mb-8">
                <TabsList className="grid w-full max-w-lg mx-auto grid-cols-2 h-auto p-2 bg-muted/50">
                  <TabsTrigger 
                    value="monthly" 
                    className="flex flex-col items-center gap-2 py-4 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
                  >
                    <Moon className="w-6 h-6" />
                    <span className="font-bold text-base">Mesečni Horoskop</span>
                    <span className="text-xs opacity-80">Maj 2026</span>
                  </TabsTrigger>
                  <TabsTrigger 
                    value="yearly" 
                    className="flex flex-col items-center gap-2 py-4 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
                  >
                    <Sun className="w-6 h-6" />
                    <span className="font-bold text-base">Godišnji Horoskop</span>
                    <span className="text-xs opacity-80">Cela 2026. godina</span>
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>

            {/* Zodiac Signs Grid */}
            <motion.div
              variants={staggerContainer}
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
              className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-12"
            >
              {ZODIAC_SIGNS.map((sign) => {
                const isAvailable = horoscopeType === 'monthly' 
                  ? MONTHLY_HOROSCOPES_APRIL_2026.some(h => h.sign === sign.name)
                  : YEARLY_HOROSCOPES_2026.some(h => h.sign === sign.name);
                
                return (
                  <motion.div key={sign.name} variants={staggerItem}>
                    <Button
                      variant={selectedZodiacSign === sign.name ? "default" : "outline"}
                      className={`w-full h-auto flex flex-col items-center gap-2 p-4 ${!isAvailable ? 'opacity-50' : ''}`}
                      onClick={() => isAvailable && setSelectedZodiacSign(sign.name)}
                      disabled={!isAvailable}
                    >
                      <span className="text-2xl">{sign.symbol}</span>
                      <span className="text-sm font-medium">{sign.name}</span>
                      <span className="text-xs text-muted-foreground">{sign.dates}</span>
                      {!isAvailable && (
                        <Badge variant="secondary" className="text-xs mt-1">Uskoro</Badge>
                      )}
                    </Button>
                  </motion.div>
                );
              })}
            </motion.div>

            {/* Horoskop Content */}
            <motion.div
              key={`${horoscopeType}-${selectedZodiacSign}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Card className="border-2">
                <CardHeader className="bg-gradient-to-r from-primary/10 to-secondary/10">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-3xl flex items-center gap-3">
                        <span className="text-4xl">
                          {ZODIAC_SIGNS.find(z => z.name === selectedZodiacSign)?.symbol}
                        </span>
                        {selectedZodiacSign}
                      </CardTitle>
                      <CardDescription className="text-base mt-2">
                        {horoscopeType === 'monthly' ? 'Mesečni Horoskop - Maj 2026' : 'Godišnji Horoskop - 2026'}
                      </CardDescription>
                    </div>
                    <Badge variant="outline" className="text-sm">
                      {ZODIAC_SIGNS.find(z => z.name === selectedZodiacSign)?.element}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="pt-8">
                  {(() => {
                    const horoscope = horoscopeType === 'monthly'
                      ? MONTHLY_HOROSCOPES_APRIL_2026.find(h => h.sign === selectedZodiacSign)
                      : YEARLY_HOROSCOPES_2026.find(h => h.sign === selectedZodiacSign);

                    if (!horoscope) {
                      return (
                        <div className="text-center py-12">
                          <Moon className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
                          <h3 className="text-xl font-semibold mb-2">Horoskop Uskoro Dostupan</h3>
                          <p className="text-muted-foreground">
                            Radimo na detaljnom horoskopu za {selectedZodiacSign}. Vratite se uskoro!
                          </p>
                        </div>
                      );
                    }

                    return (
                      <div className="prose prose-lg max-w-none dark:prose-invert">
                        {horoscope.content.split('\n\n').map((paragraph, index) => {
                          // Proveri da li je naslov (počinje sa **)
                          if (paragraph.startsWith('**') && paragraph.endsWith('**')) {
                            const title = paragraph.replace(/\*\*/g, '');
                            return (
                              <h3 key={index} className="text-2xl font-bold mt-8 mb-4 text-primary">
                                {title}
                              </h3>
                            );
                          }
                          
                          // Proveri da li je lista
                          if (paragraph.startsWith('- ')) {
                            const items = paragraph.split('\n');
                            return (
                              <ul key={index} className="space-y-2 my-4">
                                {items.map((item, i) => (
                                  <li key={i} className="text-foreground/90">
                                    {item.replace('- ', '')}
                                  </li>
                                ))}
                              </ul>
                            );
                          }

                          // Obični paragraf
                          return (
                            <p key={index} className="text-foreground/90 leading-relaxed mb-4">
                              {paragraph.split('**').map((part, i) => 
                                i % 2 === 0 ? part : <strong key={i} className="text-primary font-semibold">{part}</strong>
                              )}
                            </p>
                          );
                        })}
                      </div>
                    );
                  })()}
                </CardContent>
                <CardFooter className="bg-muted/30 flex-col items-start gap-4">
                  {/* Dugme Poruci Licni Horoskop - prikazuje se samo za mesecni horoskop */}
                  {horoscopeType === 'monthly' && (
                    <a
                      href="#contact"
                      onClick={(e) => { e.preventDefault(); document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' }); }}
                      className="w-full block"
                    >
                      <Button
                        className="w-full py-6 text-lg font-bold bg-gradient-to-r from-primary to-secondary hover:from-primary/90 hover:to-secondary/90 shadow-lg hover:shadow-xl transition-all duration-300 group"
                        size="lg"
                      >
                        <span className="text-xl mr-2">⭐</span>
                        Poruči Svoj Lični Horoskop
                        <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                      </Button>
                    </a>
                  )}
                  <div className="flex items-start gap-3 text-sm text-muted-foreground">
                    <Sparkles className="w-5 h-5 mt-0.5 flex-shrink-0 text-primary" />
                    <p>
                      Horoskopi su profesionalno napisani na osnovu realnih astroloških tranzita i pozicija planeta.
                      Za personalizovanu analizu vašeg natalnog horoskopa, poručite našu uslugu.
                    </p>
                  </div>
                </CardFooter>
              </Card>
            </motion.div>

            {/* Info Alert */}
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="mt-8"
            >
              <Alert>
                <Star className="w-4 h-4" />
                <AlertDescription>
                  <strong>Napomena:</strong> Ovi horoskopi su generalni i odnose se na sve rođene pod datim znakom. 
                  Za tačnu i detaljnu analizu koja uzima u obzir vašu tačnu poziciju Sunca, Meseca, Ascendenta i svih planeta, 
                  preporučujemo naručivanje personalizovane natalne karte.
                </AlertDescription>
              </Alert>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Instagram Feed */}
      <InstagramFeed />

      {/* Testimonials / Utisci */}
      <TestimonialsSection />

      <section id="contact" className="py-24 relative">
        <div className="absolute inset-0">
          <img 
            src={IMAGES.SPIRITUAL_1} 
            alt="" 
            className="w-full h-full object-cover opacity-5"
          />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <motion.div 
            className="max-w-4xl mx-auto"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
          >
            <motion.div variants={staggerItem} className="text-center mb-16">
              <Badge variant="secondary" className="mb-4 px-4 py-1 bg-primary/10 text-primary border-primary/20">
                Kontakt
              </Badge>
<h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Kontaktirajte Nas
              </h2>
              <p className="text-xl text-muted-foreground">
                Poručite analizu ili postavite pitanje
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-8">
              <motion.div variants={staggerItem}>
                <Card className="h-full border-primary/10">
                  <CardHeader>
                    <CardTitle style={{ fontFamily: "'Cormorant Garamond', serif" }}>Kontakt Informacije</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Mail className="w-5 h-5 text-primary" />
                      </div>
                      <div>
<div className="font-semibold mb-1">Email</div>
                        <a href="mailto:astroariesstudio@gmail.com" className="text-muted-foreground hover:text-primary transition-colors">
                          astroariesstudio@gmail.com
                        </a>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Phone className="w-5 h-5 text-primary" />
                      </div>
                      <div>
<div className="font-semibold mb-1">WhatsApp</div>
                        <a href="https://wa.me/381653471080" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-primary transition-colors">
                          +381 60 529 3755
                        </a>
                      </div>
                    </div>

<div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <svg className="w-5 h-5 text-primary" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                        </svg>
                      </div>
                      <div>
                        <div className="font-semibold mb-1">WhatsApp Grupa</div>
                        <a href="https://chat.whatsapp.com/KsGo3LAZVd16xtlwaXd6CI" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-primary transition-colors text-sm">
                          Pridružite se za dnevne savete
                        </a>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>

<motion.div variants={staggerItem}>
                <OrderForm 
                  initialDiscountCode={discountCode}
                  wheelCode={wheelCode}
                  wheelDiscount={wheelDiscount}
                />
              </motion.div>
            </div>
          </motion.div>
        </div>
</section>
</Layout>

      {/* Sticky WhatsApp Dugme - IZVAN Layout */}
      <StickyWhatsApp />
</>
  );
}